﻿﻿<script setup lang="ts">
/**
 * RailNav · 左栏（252px，按主页面切换结构）
 * ------------------------------------------------------------
 * railMode 配置化：
 * - 'chat'（默认）：新建会话 + 单开分组导航（工作会话/讨论群组/通讯录）
 * - 'sol'（/product）：产研导航（搜索 + 常用菜单 + 分组功能树），分组数据 @modules/solution/web/sol-nav，
 *   替代产研页原页内侧栏；收藏沿用 ema.sol.navfavs.v1
 * - 'arch'（/apps）：菜单搜索 + 常用菜单（收藏置顶）+ 系统菜单
 *   （业务模块 → 功能项，数据来自 app-menus.ts，与架构台账解耦）
 * 未来其他主页面（任务/日程/空间…）可在 railMode 表中扩展专属结构。
 * 收藏：功能叶子级，localStorage ema.rail.favs.v1。
 * 排序：模块（L1）/同模块功能（L2）拖拽排序，localStorage ema.rail.menuOrder.v1。
 * 收窄态（mini）：AppShell 折叠左栏时收窄为 64px 图标窄轨（复刻需求目录收窄效果），
 * 完整内容 display:none 保留状态；点图标 = 展开回全宽并定位到对应分组/模块。
 */
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSessionStore } from '@/domain/session/store'
import { authUser } from '@/data/api/auth-store'
import { useI18n } from '@/design/i18n'
import { useToast } from '@/design/ui/UToast'
import { directoryRepo, menuRepo, discussionRepo } from '@/data/api/shell-repository'
import type { DiscussionRow } from '@/data/api/discussion-repository'
import type { MenuLeaf, MenuModule, MenuLeafIndex, Member, MemberProfile, Partner, TreeNode } from '@/data/api/repository'
import { openLeafNav, menuRoute } from '@/domain/menu/nav'
import { menuRouteVisible } from '@/shell/registry'
import ChatSessionList from '@/features/workbench/ChatSessionList.vue'
import UNavTree from '@/design/ui/UNavTree.vue'
import { SOL_GROUPS, SOL_PATH, solTabFor, isSolRoute, type SolTab, type SolNavGroup } from '@modules/solution/web/sol-nav'
import { avatarColor, fmtAgo } from '@/shared/format'
import { portalBrandName } from './portal/portal'

const router = useRouter()
const route = useRoute()
const sess = useSessionStore()
const { t } = useI18n()
const toast = useToast().show

/* ---------- 收窄态（mini）：64px 图标窄轨（AppShell rail-off 时传入） ----------
   点击图标 = 展开回全宽并定位到对应分组/模块；新会话在收窄态直接可点 */
const props = defineProps<{ mini?: boolean }>()
const emit = defineEmits<{ (e: 'expand'): void }>()
const searchEl = ref<HTMLInputElement | null>(null)
function miniOpenAcc(k: AccKey) { openAcc.value = k; emit('expand') }
function miniOpenMod(code: string) {
  const n = new Set(openMods.value); n.add(code)
  openMods.value = n
  emit('expand')
}
function miniSearch() {
  emit('expand')
  /* sol 模式搜索框在 UNavTree 内（无 ref），回退用选择器聚焦 */
  nextTick(() => (searchEl.value ?? document.querySelector<HTMLInputElement>('.railnav .menu-search'))?.focus())
}

/* ---------- 产研模式（/product）：左栏直接渲染产研导航（UNavTree），点选跳路由 ---------- */
const solTab = computed(() => solTabFor(route.path))
function goSol(id: string) {
  const p = SOL_PATH[id as SolTab]
  if (p && route.path !== p) router.push(p)
}
/* 收窄态产研图标：点击仅展开回全宽（功能直达走悬停浮层，保持收窄） */
function miniExpandOnly() { emit('expand') }

/* 收窄态常用菜单磁贴：
   - arch 直接用组件内 favs（ema.rail.favs.v1，已响应式）
   - sol 收藏在 UNavTree 内部（ema.sol.navfavs.v1），左栏进入收窄态瞬间重读，保证星标变化后新鲜 */
interface SolFav { id: string; name: string; ic: string; color: string }
const solFavs = ref<SolFav[]>([])
function loadSolFavs() {
  try {
    const v = JSON.parse(localStorage.getItem('ema.sol.navfavs.v1') || '[]')
    solFavs.value = Array.isArray(v)
      ? (v as SolFav[]).slice(0, 10).filter(f => SOL_GROUPS.some(g => g.items.some(i => i.id === f.id)))
      : []
  } catch { solFavs.value = [] }
}

/* 收窄态悬停浮出下级菜单：模块→功能叶子直达；会话/群组/通讯录→对应列表直达。
   打开即时、关闭 140ms 宽限（图标与浮层间移动不断开）；展开回全宽时强制收起 */
type MiniFly =
  | { kind: 'mod'; mod: MenuModule; y: number }
  | { kind: 'acc'; key: 'chat' | 'groups' | 'dir'; y: number }
  | { kind: 'solg'; g: SolNavGroup; y: number }
type MiniFlyBase =
  | { kind: 'mod'; mod: MenuModule }
  | { kind: 'acc'; key: 'chat' | 'groups' | 'dir' }
  | { kind: 'solg'; g: SolNavGroup }
const miniFly = ref<MiniFly | null>(null)
let flyTimer: ReturnType<typeof setTimeout> | undefined
function flyKeep() { if (flyTimer) { clearTimeout(flyTimer); flyTimer = undefined } }
function flyCancel() { flyKeep(); miniFly.value = null }
function flyCloseSoon() { flyTimer = setTimeout(() => { miniFly.value = null }, 140) }
function flyFromBtn(e: MouseEvent, f: MiniFlyBase) {
  flyKeep()
  miniFly.value = { ...f, y: (e.currentTarget as HTMLElement).getBoundingClientRect().top }
}
function flyStyle(y: number) { return { left: '68px', top: Math.min(y, Math.max(12, window.innerHeight - 380)) + 'px' } }
watch(() => props.mini, v => {
  if (!v) { flyCancel(); return }
  if (railMode.value === 'sol') loadSolFavs() /* 收窄瞬间刷新收藏磁贴 */
}) /* immediate 不可用：回调引用的 railMode 声明在下方，会触发 TDZ；初始装载由 onMounted 兜底 */

const recent = computed(() => sess.visibleSessions)

/* ---------- 会话左栏主体：单开分组（一次只展开一组）：工作会话 / 讨论群组 / 通讯录 ---------- */
type AccKey = 'chat' | 'groups' | 'dir'
const openAcc = ref<AccKey | ''>('chat')
function togAcc(k: AccKey) { openAcc.value = openAcc.value === k ? '' : k }

/* 讨论群组：我参与的真实讨论组（/api/discussions）；状态点 进行中=accent / 已归档=灰；未读数随打开讨论清除 */
const tmpGroups = ref<DiscussionRow[]>([])
const discError = ref('')
async function loadDiscussions() {
  if (!authUser.value) { tmpGroups.value = []; return }
  try { tmpGroups.value = await discussionRepo.list(); discError.value = '' }
  catch (e) { discError.value = e instanceof Error ? e.message : '讨论列表读取未完成'; tmpGroups.value = [] }
}
/* 登录即取一次；展开分组 / 全局搜索 / 进出讨论页时刷新未读与排序 */
watch([() => authUser.value?.personId, openAcc, () => route.query.discussion], () => { void loadDiscussions() }, { immediate: true })
function openDiscussion(id: string) { router.push({ path: '/chat', query: { discussion: id } }) }
/* 讨论群组按 kind 分「临时讨论 / 固定群聊」两个页签；均为真实讨论组（/api/discussions），共用同一讨论视图与结论形式 */
const tempGroups = computed(() => tmpGroups.value.filter(g => g.kind !== 'fixed'))
const fixGroups = computed(() => tmpGroups.value.filter(g => g.kind === 'fixed'))
/* 讨论群组页签 */
const grpTab = ref<'tmp' | 'fix'>('tmp')

/* 悬停完整信息卡（原生 title 提示慢且简陋；Teleport 到 body，跟随鼠标右下角） */
const hoverCard = ref<null | { x: number; y: number; title: string; sub: string; color: string; mems: { id: string; name: string }[] }>(null)
function hovTmp(e: MouseEvent, tp: DiscussionRow) {
  hoverCard.value = {
    x: e.clientX, y: e.clientY, title: tp.title,
    sub: `${tp.status === 'active' ? '进行中' : '已归档'} · ${tp.member_count}人${tp.unread ? ` · ${tp.unread}条未读` : ''}`,
    color: tp.status === 'active' ? 'var(--accent)' : 'var(--ink-4)',
    mems: []
  }
}
function hovFix(e: MouseEvent, g: DiscussionRow) {
  hoverCard.value = { x: e.clientX, y: e.clientY, title: g.title, sub: `固定群聊 · ${g.member_count}人${g.unread ? ` · ${g.unread}条未读` : ''}`, color: '#3b82f6', mems: [] }
}
function hovMove(e: MouseEvent) {
  if (hoverCard.value) { hoverCard.value.x = e.clientX; hoverCard.value.y = e.clientY }
}
function hovOut() { hoverCard.value = null }
function hcStyle() {
  const c = hoverCard.value!
  return {
    left: Math.min(c.x + 14, window.innerWidth - 280) + 'px',
    top: Math.min(c.y + 14, window.innerHeight - 150) + 'px'
  }
}

/* ---------- 企业通讯录（聊天左栏内嵌全功能；数据经 directoryRepo，M2 接口化） ---------- */
const dirTree = ref<TreeNode>({ label: '', count: 0, path: '', children: [] })
const dirStar = ref<Member[]>([])
const dirMembers = ref<Member[]>([])
const partners = ref<Partner[]>([])
const dirTab = ref<'org' | 'star' | 'ext'>('org')
const dirKw = ref('')
const dirSel = ref<Member | null>(null)
/* 默认只展开第一个中心（同层互斥自动折叠），树最后一级到人；目录数据到达后初始化 */
const dirOpen = ref<Set<string>>(new Set())
/* 组织树：根节点=当前租户最高级公司（portalBrandName），公司 → 中心 → 部门 → 人；
   默认只展开第一个中心（自动折叠口径），其余中心点击时互斥展开 */
const dirBusy=ref(false),dirError=ref(''),profileError=ref('')
let dirGeneration=0,profileGeneration=0
async function loadDirectory(){
 const ticket=++dirGeneration;profileGeneration++;dirBusy.value=true;dirError.value='';dirSel.value=null;dirProf.value=null
 dirMembers.value=[];partners.value=[];dirStar.value=[];dirTree.value={label:'',path:'directory',count:0,children:[]}
 try{const [members,tree]=await Promise.all([directoryRepo.members(),directoryRepo.orgTree(portalBrandName.value)]);if(ticket!==dirGeneration)return;dirMembers.value=members;dirTree.value=tree;dirOpen.value=new Set([tree.path,...(tree.children||[]).map(d=>d.path)]);
 const [recents,pts]=await Promise.all([directoryRepo.recents(),directoryRepo.partners()]);if(ticket!==dirGeneration)return
 dirStar.value=recents;partners.value=pts
 }catch(e){if(ticket===dirGeneration){dirError.value=(e as Error).message;dirMembers.value=[];dirTree.value={label:'',path:'directory',count:0,children:[]}}}
 finally{if(ticket===dirGeneration)dirBusy.value=false}
}
watch([portalBrandName,()=>authUser.value?.personId],()=>{void loadDirectory()})
queueMicrotask(()=>{void loadDirectory()})
onBeforeUnmount(()=>{dirGeneration++;profileGeneration++})
/* 自动折叠（手风琴）：同层互斥 —— 展开某中心/部门时自动收起其兄弟；
   展开中心时其下部门自动全开；点击已展开的节点仅收起自身 */
function togDir(p: string) {
  const n = new Set(dirOpen.value)
  if (n.has(p)) { n.delete(p); dirOpen.value = n; return }
  for (const c of dirTree.value.children ?? []) {
    if (c.path === p) {
      for (const s of dirTree.value.children ?? []) {
        if (s.path === p) continue
        n.delete(s.path)
        for (const d of s.children ?? []) n.delete(d.path)
      }
      for (const d of c.children ?? []) n.add(d.path)
      break
    }
    if ((c.children ?? []).some(d => d.path === p)) {
      for (const s of c.children ?? []) if (s.path !== p) n.delete(s.path)
      break
    }
  }
  n.add(p)
  dirOpen.value = n
}
/* 搜索：跨内部+外部（姓名/职位/部门/公司/角色） */
function dirMessage(_m: Member) { toast('人员会话发送尚未接入，没有发送消息。') }
const dirProf = ref<MemberProfile | null>(null)
async function openMember(m: Member) {
 const ticket=++profileGeneration;dirSel.value=m;dirProf.value=null;profileError.value=''
 try{const p=await directoryRepo.profile(m);if(ticket===profileGeneration)dirProf.value=p}catch(e){if(ticket===profileGeneration)profileError.value=(e as Error).message}
}

/* ---------- 懒加载：每批 10 行，滚动触底出现「加载中…」后继续取下一批 ----------
   树/列表先扁平成行序列，再按渲染窗口切片（千人体量不整树渲染）；展开/收起改变序列，窗口自动收敛。 */
interface DirRow { key: string; kind: 'center' | 'dept' | 'person'; label: string; count: number; path: string; open: boolean; pad: number; hasKids: boolean; trail: string; member?: Member }
/* 树行缩进（支持 7 级部门）：前几级正常 12/10/8px 递进，深级只再让 5/4/4px，
   既保持层级递增，又不把窄栏右侧的文字挤没；超过 7 级不再缩进 */
const DIR_PADS = [16, 28, 38, 46, 52, 57, 61, 65]
function dirPad(depth: number) { return DIR_PADS[Math.min(depth, DIR_PADS.length - 1)] }
const dirRows = computed<DirRow[]>(() => {
  const rows: DirRow[] = []
  function walk(nodes:TreeNode[],depth:number,trail:string){for(const n of nodes){const person=!!n.member;rows.push({key:n.path,kind:person?'person':depth===0?'center':'dept',label:n.label,count:n.count,path:n.path,open:dirOpen.value.has(n.path),pad:dirPad(depth),hasKids:!!(!person&&n.children?.length),trail,member:n.member});if(!person&&dirOpen.value.has(n.path))walk(n.children||[],depth+1,trail?trail+' / '+n.label:n.label)}}
  if(dirOpen.value.has(dirTree.value.path))walk(dirTree.value.children||[],0,'')
  return rows
})
interface ExtRow { key: string; name: string; title: string; co: string; member: Member }
/* 外部伙伴按人平铺（与「常用」一致）：仅「我负责」的伙伴成员可见；未负责公司的人不进入列表 */
const extRows = computed<ExtRow[]>(() =>
  partners.value.filter(pt => pt.managed).flatMap(pt =>
    pt.members.map(m => ({ key: m.id, name: m.name, title: m.title, co: pt.name, member: m }))
  )
)
/* 搜索：跨内部+外部（姓名/职位/部门/公司/角色） */
const dirHits = computed<Member[]>(() => {
  const k = dirKw.value.trim().toLowerCase()
  if (!k) return []
  return [...dirMembers.value, ...extRows.value.map(r => r.member)].filter(m =>
    (m.name + m.title + m.dept + (m.co ?? '') + m.roles.join('')).toLowerCase().includes(k))
})
const DIR_INIT = 18   /* 初始批量需填满 46vh 容器（否则无滚动空间，触底加载永不触发） */

/* ---------- 左栏全局搜索：工作会话（服务端）+ 讨论群组 + 通讯录人员 ---------- */
const railKw = ref('')
let railTimer: ReturnType<typeof setTimeout> | undefined
watch(railKw, v => {
  dirKw.value = v
  if (railTimer) clearTimeout(railTimer)
  railTimer = setTimeout(() => { void sess.searchSessions(v.trim()) }, 220)
})
onBeforeUnmount(() => { if (railTimer) clearTimeout(railTimer) })
const railSess = computed(() => (railKw.value.trim() ? recent.value.slice(0, 6) : []))
const railGroups = computed(() => {
  const k = railKw.value.trim().toLowerCase()
  if (!k) return []
  return [
    ...tempGroups.value.filter(g => g.title.toLowerCase().includes(k)).map(g => ({ key: g.id, name: g.title, note: `${g.status === 'active' ? '进行中' : '已归档'} · ${g.member_count}人` })),
    ...fixGroups.value.filter(g => g.title.toLowerCase().includes(k)).map(g => ({ key: g.id, name: g.title, note: `固定群聊 · ${g.member_count}人` })),
  ].slice(0, 6)
})
const railPeople = computed(() => (railKw.value.trim() ? dirHits.value.slice(0, 6) : []))
async function openHitSession(id: string) { await sess.openSession(id); router.push('/chat') }
const DIR_STEP = 10
function mkLoader(total: () => number) {
  const shown = ref(DIR_INIT)
  const loading = ref(false)
  const hasMore = computed(() => total() > shown.value)
  /* 数据源收敛（搜索词变化/收起节点）→ 窗口不越界、加载态复位 */
  watch(total, t => { shown.value = Math.min(shown.value, Math.max(t, DIR_INIT)); loading.value = false })
  function scroll(e: Event) {
    const el = e.target as HTMLElement
    if (!hasMore.value || loading.value) return
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 4) return
    loading.value = true
    window.setTimeout(() => { shown.value += DIR_STEP; loading.value = false }, 380)
  }
  return reactive({ shown, loading, hasMore, scroll })
}
const orgLd = mkLoader(() => dirRows.value.length)
const hitLd = mkLoader(() => dirHits.value.length)
const starLd = mkLoader(() => dirStar.value.length)
const extLd = mkLoader(() => extRows.value.length)

/* ---------- rail 模式：聊天页=会话左栏（单开分组导航），产研页=产研导航树，其余应用菜单页=系统菜单树。RailNav 仅在有左栏的页面渲染（AppShell railPage 控制） ---------- */
const railMode = computed<'chat' | 'arch' | 'sol'>(() =>
  route.path === '/chat' ? 'chat' : (isSolRoute(route.path) ? 'sol' : 'arch'))
/* 页面以收窄态打开（折叠偏好持久化）时，装载一次产研收藏磁贴；此后收窄切换由 watch(props.mini) 刷新 */
onMounted(() => { if (props.mini && railMode.value === 'sol') loadSolFavs() })

/* 工作会话列表不等聊天页组件就绪：进入聊天页就先取一次真实会话（列表为空时），
   聊天页随后按 2.5s 轮询增量刷新；失败由聊天页轮询重试 */
watch(railMode, m => {
  if (m === 'chat' && !sess.sessions.length) void sess.refreshChat().catch(() => { /* 由聊天页轮询重试 */ })
}, { immediate: true })

function newChat() {
  sess.newSession()
  router.push('/chat')
}

/* ---------- 系统菜单：常用菜单（仅手动点行尾☆收藏；固定上限 10 个，与使用行为无关） ---------- */
const FAV_KEY = 'ema.rail.favs.v1'
const FAV_MAX = 10
interface Fav { code: string; name: string; ic: string; color: string }
function loadRawFavs(): Fav[] {
  try {
    const v = JSON.parse(localStorage.getItem(FAV_KEY) || '[]')
    return Array.isArray(v) ? v.slice(0, FAV_MAX) : []
  } catch { return [] }
}
const favs = ref<Fav[]>(loadRawFavs())
watch(favs, v => { try { localStorage.setItem(FAV_KEY, JSON.stringify(v)) } catch { /* 忽略 */ } }, { deep: true })
/* 系统菜单（经 menuRepo，M2 接口化）；数据到达后过滤失效收藏（菜单调整后旧收藏自动失效） */
const menus = ref<MenuModule[]>([])
const leafBy = ref<MenuLeafIndex>({})
const menuError = ref('')
const menuLoading = ref(false)
async function loadMenus() {
  if (menuLoading.value) return
  menuLoading.value = true
  menuError.value = ''
  try {
    const data = await menuRepo.load()
    /* 模块开关前端兜底过滤：route 指向未启用模块的叶子隐藏（服务端已按模块过滤，
       这里再兜一层，防旧缓存/异常数据）；过滤后空模块整组不显示，收藏索引同步重建 */
    const mods = data.modules
      .map(m => ({ ...m, items: m.items.filter(l => !l.route || menuRouteVisible(menuRoute(l.route))) }))
      .filter(m => m.items.length > 0)
    const ordered = applySavedOrder(mods)
    menus.value = ordered
    const idx: MenuLeafIndex = {}
    for (const m of ordered) for (const l of m.items) idx[l.id] = { leaf: l, mod: m }
    leafBy.value = idx
    favs.value = favs.value.filter(f => !!idx[f.code])
  } catch {
    menus.value = []
    leafBy.value = {}
    menuError.value = '菜单暂时不可用，请重试'
  } finally { menuLoading.value = false }
}
void loadMenus()

function isFav(id: string) { return favs.value.some(f => f.code === id) }
function toggleFav(e: Event, l: MenuLeaf, m: MenuModule) {
  e.stopPropagation()
  const i = favs.value.findIndex(f => f.code === l.id)
  if (i >= 0) favs.value = favs.value.filter((_, k) => k !== i)
  else if (favs.value.length >= FAV_MAX) { toast(`常用菜单最多固定 ${FAV_MAX} 个，请先取消一些再收藏`); return }
  else favs.value = [...favs.value, { code: l.id, name: l.name, ic: m.ic, color: m.color }]
}
/* 叶子导航：route 页面 / app 应用工作台 / stub → 建设中占位页（openLeafNav 领域服务统一口径，
   与 AppView.openLeaf 同一份逻辑）；页面在菜单右侧主区域刷新（菜单保留），每个叶子各成一签便于切换定位 */
function goLeaf(l: MenuLeaf) {
  openLeafNav(router, l)
}

/* ---------- 菜单搜索（功能名/模块名过滤，跨模块平铺命中） ---------- */
const menuKw = ref('')
const menuHits = computed(() => {
  const kw = menuKw.value.trim().toLowerCase()
  if (!kw) return null
  const out: { leaf: MenuLeaf; mod: MenuModule }[] = []
  for (const m of menus.value) {
    for (const l of m.items) {
      if (l.name.toLowerCase().includes(kw) || m.name.toLowerCase().includes(kw))
        out.push({ leaf: l, mod: m })
    }
  }
  return out
})

/* ---------- 系统菜单树：模块展开状态 + 当前项高亮 ---------- */
const openMods = ref<Set<string>>(new Set())
function togMod(code: string) {
  const n = new Set(openMods.value); n.has(code) ? n.delete(code) : n.add(code)
  openMods.value = n
}
/* 一键展开/收起全部模块：已全开时点击=全部收起，否则=全部展开 */
const allModsOpen = computed(() => menus.value.length > 0 && menus.value.every(m => openMods.value.has(m.code)))
function togAllMods() {
  openMods.value = allModsOpen.value ? new Set() : new Set(menus.value.map(m => m.code))
}
/* 叶子高亮：route（含 query 逐项比对）/ app 工作台 */
function leafOn(l: MenuLeaf) {
  if (l.app) return String(route.params.appCode || '') === l.app
  if (!l.route) return false
  const [p, qs] = menuRoute(l.route).split('?')
  if (route.path !== p) return false
  for (const [k, v] of new URLSearchParams(qs || '')) {
    if (String(route.query[k] || '') !== v) return false
  }
  return true
}

/* ---------- 菜单拖拽排序：L1 模块间 / L2 同模块内；顺序存 localStorage，加载时套用到最新菜单 ---------- */
const ORDER_KEY = 'ema.rail.menuOrder.v1'
interface MenuOrder { mods: string[]; leaves: Record<string, string[]> }
function loadOrder(): MenuOrder {
  try {
    const v = JSON.parse(localStorage.getItem(ORDER_KEY) || '{}')
    return { mods: Array.isArray(v.mods) ? v.mods : [], leaves: v.leaves && typeof v.leaves === 'object' ? v.leaves : {} }
  } catch { return { mods: [], leaves: {} } }
}
const menuOrder = ref<MenuOrder>(loadOrder())
function persistOrder() {
  const leaves: Record<string, string[]> = {}
  for (const m of menus.value) leaves[m.code] = m.items.map(l => l.id)
  const v: MenuOrder = { mods: menus.value.map(m => m.code), leaves }
  menuOrder.value = v
  try { localStorage.setItem(ORDER_KEY, JSON.stringify(v)) } catch { /* 忽略 */ }
}
/* 已保存顺序套用到服务端菜单：稳定排序，新出现的模块/功能保持原顺序排在已排序项之后 */
function applySavedOrder(mods: MenuModule[]): MenuModule[] {
  const saved = menuOrder.value
  const mr = saved.mods
  const out = [...mods].sort((a, b) => {
    const ia = mr.indexOf(a.code), ib = mr.indexOf(b.code)
    return (ia < 0 ? mr.length : ia) - (ib < 0 ? mr.length : ib)
  })
  for (const m of out) {
    const lr = saved.leaves[m.code]
    if (!lr?.length) continue
    m.items = [...m.items].sort((a, b) => {
      const ia = lr.indexOf(a.id), ib = lr.indexOf(b.id)
      return (ia < 0 ? lr.length : ia) - (ib < 0 ? lr.length : ib)
    })
  }
  return out
}

type DragKey = { kind: 'mod'; code: string } | { kind: 'leaf'; mod: string; id: string }
const dragKey = ref<DragKey | null>(null)
const dropHint = ref<{ key: string; before: boolean } | null>(null)
function isDraggingMod(code: string) { const d = dragKey.value; return d?.kind === 'mod' && d.code === code }
function isDraggingLeaf(id: string) { const d = dragKey.value; return d?.kind === 'leaf' && d.id === id }
function hintCls(key: string) { const h = dropHint.value; return h?.key === key ? (h.before ? 'drop-before' : 'drop-after') : '' }
function dragStartMod(e: DragEvent, m: MenuModule) {
  dragKey.value = { kind: 'mod', code: m.code }
  if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', m.code) }
}
function dragStartLeaf(e: DragEvent, l: MenuLeaf, m: MenuModule) {
  dragKey.value = { kind: 'leaf', mod: m.code, id: l.id }
  if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', l.id) }
}
/* 悬停行上下半区决定插入前/后；类型不匹配（模块拖到功能上、跨模块拖功能）不接受放置 */
function dragOverRow(e: DragEvent, key: string, accept: 'mod' | 'leaf', modCode?: string) {
  const d = dragKey.value
  if (!d || d.kind !== accept) return
  if (d.kind === 'leaf' && modCode && d.mod !== modCode) return
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  const before = e.clientY < r.top + r.height / 2
  if (dropHint.value?.key !== key || dropHint.value.before !== before) dropHint.value = { key, before }
}
function dropOnMod(target: MenuModule) {
  const d = dragKey.value
  if (!d || d.kind !== 'mod' || d.code === target.code) { dragEnd(); return }
  const dragged = menus.value.find(m => m.code === d.code)
  if (!dragged) { dragEnd(); return }
  const rest = menus.value.filter(m => m.code !== d.code)
  let idx = rest.findIndex(m => m.code === target.code)
  if (idx < 0) idx = rest.length
  else if (dropHint.value?.key === 'm:' + target.code && !dropHint.value.before) idx++
  rest.splice(idx, 0, dragged)
  menus.value = rest
  persistOrder()
  dragEnd()
}
function dropOnLeaf(target: MenuLeaf, mod: MenuModule) {
  const d = dragKey.value
  if (!d || d.kind !== 'leaf' || d.mod !== mod.code || d.id === target.id) { dragEnd(); return }
  const dragged = mod.items.find(l => l.id === d.id)
  if (!dragged) { dragEnd(); return }
  const items = mod.items.filter(l => l.id !== d.id)
  let idx = items.findIndex(l => l.id === target.id)
  if (idx < 0) idx = items.length
  else if (dropHint.value?.key === 'l:' + target.id && !dropHint.value.before) idx++
  items.splice(idx, 0, dragged)
  menus.value = menus.value.map(m => (m.code === mod.code ? { ...m, items } : m))
  persistOrder()
  dragEnd()
}
function dragEnd() { dragKey.value = null; dropHint.value = null }
</script>

<template>
  <div class="railnav" :class="{ mini }">
    <!-- 品牌已上移至顶部页签条（WorkContextBar），左栏直接从功能区分开始 -->

    <!-- ============ 收窄态图标窄轨（复刻需求目录收窄效果）：仅 mini 时渲染 ============ -->
    <div v-if="mini" class="rail-mini">
      <template v-if="railMode === 'chat'">
        <button class="rmi accent" title="新建会话（Ctrl+K）" @click="newChat" @mouseenter="flyCancel">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3.2v9.6M3.2 8h9.6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
          <span class="rm-nm">新会话</span>
        </button>
        <button class="rmi" :class="{ on: openAcc === 'chat' }" title="工作会话" @click="miniOpenAcc('chat')"
                @mouseenter="flyFromBtn($event, { kind: 'acc', key: 'chat' })" @mouseleave="flyCloseSoon">
          <svg viewBox="0 0 24 24" width="16" height="16"><path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.3c-1.5 0-2.9-.3-4.1-1L3 20l1.3-4.1A8.2 8.2 0 0 1 3.5 11.5 8.4 8.4 0 0 1 12 3.2a8.4 8.4 0 0 1 9 8.3Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>
          <span class="rm-nm">会话</span>
        </button>
        <button class="rmi" :class="{ on: openAcc === 'groups' }" title="讨论群组" @click="miniOpenAcc('groups')"
                @mouseenter="flyFromBtn($event, { kind: 'acc', key: 'groups' })" @mouseleave="flyCloseSoon">
          <svg viewBox="0 0 24 24" width="16" height="16"><circle cx="9" cy="8.5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3.5 19c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="16.8" cy="9.5" r="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M16.5 14.6c2.3.2 3.8 1.6 4.3 3.9" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
          <span class="rm-nm">群组</span>
        </button>
        <button class="rmi" :class="{ on: openAcc === 'dir' }" title="通讯录" @click="miniOpenAcc('dir')"
                @mouseenter="flyFromBtn($event, { kind: 'acc', key: 'dir' })" @mouseleave="flyCloseSoon">
          <svg viewBox="0 0 24 24" width="16" height="16"><rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="9" cy="11" r="2.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M5.8 16.2c.5-1.6 1.8-2.4 3.2-2.4s2.7.8 3.2 2.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M14.5 9.5H18M14.5 13h3.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
          <span class="rm-nm">通讯录</span>
        </button>
        <button class="rmi" title="搜索会话 / 群组 / 人员" @click="miniSearch" @mouseenter="flyCancel">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/></svg>
          <span class="rm-nm">搜索</span>
        </button>
      </template>
      <template v-else-if="railMode === 'sol'">
        <button class="rmi" title="搜索菜单 / 功能" @click="miniSearch" @mouseenter="flyCancel">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/></svg>
          <span class="rm-nm">搜索</span>
        </button>
        <!-- 收藏的常用菜单：收窄态直接平铺，一步到位直达（保持收窄） -->
        <button v-for="f in solFavs" :key="f.id" class="rmi fav"
                :class="{ on: f.id === solTab }" :title="`常用 · ${f.name}`"
                @click="goSol(f.id)" @mouseenter="flyCancel">
          <span class="rm-ic" :style="{ background: f.color }">{{ f.ic }}</span>
          <span class="rm-nm">{{ f.name }}</span>
        </button>
        <div v-if="solFavs.length" class="rm-sep" />
        <button v-for="g in SOL_GROUPS" :key="g.id" class="rmi"
                :class="{ on: g.items.some(i => i.id === solTab) }" :title="g.name"
                @click="miniExpandOnly"
                @mouseenter="flyFromBtn($event, { kind: 'solg', g })" @mouseleave="flyCloseSoon">
          <span class="rm-ic" :style="{ background: g.color }">{{ g.ic }}</span>
          <span class="rm-nm">{{ g.name }}</span>
        </button>
      </template>
      <template v-else-if="railMode === 'arch'">
        <button class="rmi" title="搜索菜单 / 功能" @click="miniSearch" @mouseenter="flyCancel">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/></svg>
          <span class="rm-nm">搜索</span>
        </button>
        <!-- 收藏的常用菜单：收窄态直接平铺，一步到位直达（保持收窄） -->
        <button v-for="f in favs" :key="f.code" class="rmi fav"
                :class="{ on: leafBy[f.code] ? leafOn(leafBy[f.code].leaf) : false }" :title="`常用 · ${f.name}`"
                @click="leafBy[f.code] && goLeaf(leafBy[f.code].leaf)" @mouseenter="flyCancel">
          <span class="rm-ic" :style="{ background: f.color }">{{ f.ic }}</span>
          <span class="rm-nm">{{ f.name }}</span>
        </button>
        <div v-if="favs.length" class="rm-sep" />
        <button v-for="m in menus" :key="m.code" class="rmi"
                :class="{ on: m.items.some(leafOn) }" :title="m.name"
                @click="miniOpenMod(m.code)"
                @mouseenter="flyFromBtn($event, { kind: 'mod', mod: m })" @mouseleave="flyCloseSoon">
          <span class="rm-ic" :style="{ background: m.color }">{{ m.ic }}</span>
          <span class="rm-nm">{{ m.name }}</span>
        </button>
      </template>
    </div>

    <!-- ============ 聊天模式（默认：单开分组导航） ============ -->
    <template v-if="railMode === 'chat'">
      <!-- 新建会话 -->
      <button class="new-chat" @click="newChat">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M8 3.2v9.6M3.2 8h9.6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
        </svg>
        <span>{{ t.nav.newChat }}</span> <span class="k">Ctrl+K</span>
      </button>

      <!-- 全局搜索：工作会话 / 讨论群组 / 通讯录人员 -->
      <input ref="searchEl" v-model="railKw" class="menu-search" type="search" maxlength="40" placeholder="搜索会话 / 群组 / 人员…" aria-label="搜索会话、群组和人员">

      <!-- 搜索结果：命中的会话 / 群组 / 人员平铺，点击直达 -->
      <div v-if="railKw.trim()" class="rail-hits">
        <template v-if="railSess.length">
          <div class="rh-label">工作会话</div>
          <button v-for="s in railSess" :key="s.id" class="rh-row" @click="openHitSession(s.id)">
            <span class="rh-dot" :data-health="s.health || 'ok'" />
            <b class="rh-nm">{{ s.title }}</b>
          </button>
        </template>
        <template v-if="railGroups.length">
          <div class="rh-label">讨论群组</div>
          <button v-for="g in railGroups" :key="g.key" class="rh-row" @click="openDiscussion(g.key)">
            <span class="rh-dot" />
            <b class="rh-nm">{{ g.name }}</b>
            <small class="rh-note">{{ g.note }}</small>
          </button>
        </template>
        <template v-if="railPeople.length">
          <div class="rh-label">通讯录</div>
          <button v-for="m in railPeople" :key="m.id" class="rh-row" @click="openMember(m)">
            <span class="rh-av" :style="{ background: avatarColor(m.name) }">{{ m.name[0] }}</span>
            <b class="rh-nm">{{ m.name }}</b>
            <small class="rh-note">{{ m.title || m.dept }}</small>
          </button>
        </template>
        <div v-if="!railSess.length && !railGroups.length && !railPeople.length" class="rail-empty">没有匹配的内容</div>
      </div>

      <!-- ============ 单开分组（一次展开一组）：工作会话 / 讨论群组 / 通讯录 ============ -->
      <div v-show="!railKw.trim()" class="acc-list">
        <!-- 工作会话 -->
        <section class="acc" :class="{ open: openAcc === 'chat', on: openAcc === 'chat' }">
          <button class="acc-head" title="工作会话" @click="togAcc('chat')">
            <svg class="acc-ic" viewBox="0 0 24 24" width="15" height="15"><path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.3c-1.5 0-2.9-.3-4.1-1L3 20l1.3-4.1A8.2 8.2 0 0 1 3.5 11.5 8.4 8.4 0 0 1 12 3.2a8.4 8.4 0 0 1 9 8.3Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>
            <span class="acc-nm">工作会话</span>
            <em class="acc-ct">{{ recent.length || '' }}</em>
            <i class="acc-arr">›</i>
          </button>
          <div v-show="openAcc === 'chat'" class="acc-body chats">
            <ChatSessionList :searchable="false" @picked="router.push('/chat')" />
          </div>
        </section>

        <!-- 讨论群组（临时讨论 / 固定群组 双页签） -->
        <section class="acc" :class="{ open: openAcc === 'groups', on: openAcc === 'groups' }">
          <button class="acc-head" title="讨论群组" @click="togAcc('groups')">
            <svg class="acc-ic" viewBox="0 0 24 24" width="15" height="15"><circle cx="9" cy="8.5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3.5 19c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="16.8" cy="9.5" r="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M16.5 14.6c2.3.2 3.8 1.6 4.3 3.9" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
            <span class="acc-nm">讨论群组</span>
            <em class="acc-ct">{{ tmpGroups.length || '' }}</em>
            <i class="acc-arr">›</i>
          </button>
          <div v-show="openAcc === 'groups'" class="acc-body">
            <div class="tp-tabs">
              <button :class="{ on: grpTab === 'tmp' }" @click="grpTab = 'tmp'">临时讨论</button>
              <button :class="{ on: grpTab === 'fix' }" @click="grpTab = 'fix'">固定群组</button>
            </div>
            <!-- 临时讨论：真实讨论组（状态点 + 成员数 + 未读徽标），点击进入讨论视图 -->
            <template v-if="grpTab === 'tmp'">
              <p v-if="discError" class="rail-empty" role="alert">{{ discError }} <button type="button" @click="loadDiscussions">重试</button></p>
              <button v-for="tp in tempGroups" :key="tp.id" class="topic" @click="openDiscussion(tp.id)"
                      @mouseenter="hovTmp($event, tp)" @mousemove="hovMove" @mouseleave="hovOut">
                <span class="tp-dot" :style="{ background: tp.status === 'active' ? 'var(--accent)' : 'var(--ink-4)' }" />
                <span class="tp-b">
                  <b class="tp-t">{{ tp.title }}</b>
                  <small>{{ tp.status === 'active' ? '进行中' : '已归档' }} · {{ tp.member_count }}人</small>
                </span>
                <span v-if="tp.unread" class="tp-unread">{{ tp.unread > 99 ? '99+' : tp.unread }}</span>
              </button>
              <div v-if="!discError && !tempGroups.length" class="rail-empty">暂无讨论，右键会话可发起讨论</div>
            </template>
            <!-- 固定群聊：真实讨论组（kind=fixed），与临时讨论共用讨论视图 -->
            <template v-else>
              <p v-if="discError" class="rail-empty" role="alert">{{ discError }} <button type="button" @click="loadDiscussions">重试</button></p>
              <button v-for="g in fixGroups" :key="g.id" class="topic" @click="openDiscussion(g.id)"
                      @mouseenter="hovFix($event, g)" @mousemove="hovMove" @mouseleave="hovOut">
                <span class="tp-dot" style="background:#3b82f6" />
                <span class="tp-b">
                  <b class="tp-t">{{ g.title }}</b>
                  <small>固定群聊 · {{ g.member_count }}人</small>
                </span>
                <span v-if="g.unread" class="tp-unread">{{ g.unread > 99 ? '99+' : g.unread }}</span>
              </button>
              <div v-if="!discError && !fixGroups.length" class="rail-empty">暂无固定群聊</div>
            </template>
          </div>
        </section>

        <!-- 通讯录（内嵌全功能：搜索 / 组织架构到人 / 常用 / 外部伙伴） -->
        <section class="acc" :class="{ open: openAcc === 'dir', on: openAcc === 'dir' }">
          <button class="acc-head" title="通讯录" @click="togAcc('dir')">
            <svg class="acc-ic" viewBox="0 0 24 24" width="15" height="15"><rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="9" cy="11" r="2.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M5.8 16.2c.5-1.6 1.8-2.4 3.2-2.4s2.7.8 3.2 2.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M14.5 9.5H18M14.5 13h3.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
            <span class="acc-nm">通讯录</span>
            <em class="acc-ct"></em>
            <i class="acc-arr">›</i>
          </button>
          <div v-show="openAcc === 'dir'" class="acc-body dir-body">
            <p v-if="dirError" role="alert">{{dirError}}</p>
            <p v-if="dirBusy">正在读取人员…</p>
            <div class="dir-search">
              <input v-model="dirKw" class="menu-search" type="text" placeholder="搜索姓名 / 职位 / 部门 / 公司…" maxlength="20">
            </div>

            <!-- 搜索命中：跨内部+外部平铺（懒加载，每批 10 条） -->
            <div v-if="dirKw.trim()" class="dir-list" @scroll="hitLd.scroll">
              <button v-for="m in dirHits.slice(0, hitLd.shown)" :key="m.id" class="dir-p" @click="openMember(m)">
                <span class="dp-av" :style="{ background: avatarColor(m.name) }">{{ m.name[0] }}<i v-if="m.online" class="dp-on" /></span>
                <span class="dp-b"><b>{{ m.name }}<i v-if="m.external" class="dp-ext">外</i></b><small v-if="m.title || m.dept || m.co">{{ m.title || m.dept }}<template v-if="m.co"> · {{ m.co }}</template></small></span>
              </button>
              <div v-if="hitLd.loading" class="dir-more"><i class="dir-spin" />加载中…</div>
              <div v-else-if="!dirHits.length" class="dir-empty">没有匹配的成员</div>
            </div>

            <template v-else>
              <div class="dir-tabs">
                <button :class="{ on: dirTab === 'org' }" @click="dirTab = 'org'">组织架构</button>
                <button :class="{ on: dirTab === 'star' }" @click="dirTab = 'star'">常用</button>
                <button :class="{ on: dirTab === 'ext' }" @click="dirTab = 'ext'">外部</button>
              </div>

              <!-- 组织架构：公司（根）→ 任意层级部门 → 人；千人体量按渲染窗口懒加载（每批 10 行，滚动触底续载） -->
              <div v-if="dirTab === 'org'" class="dir-list" @scroll="orgLd.scroll">
                <button class="dir-c root" :title="`${dirTree.label} ${dirTree.count} 人`" @click="togDir(dirTree.path)">
                  <i class="dir-arr" :class="{ open: dirOpen.has(dirTree.path) }">{{ dirTree.children?.length ? '▶' : '' }}</i>
                  <b>{{ dirTree.label }}</b>
                  <em>{{ dirTree.count }}</em>
                </button>
                <template v-for="r in dirRows.slice(0, orgLd.shown)" :key="r.key">
                  <button v-if="r.kind === 'center'" class="dir-c lv2" :style="{ '--pad': r.pad + 'px' }" :title="`${r.trail ? r.trail + ' / ' : ''}${r.label} · ${r.count} 人`" @click="togDir(r.path)">
                    <i class="dir-arr" :class="{ open: r.open }">{{ r.hasKids ? '▶' : '' }}</i>
                    <b>{{ r.label }}</b>
                    <em>{{ r.count }}</em>
                  </button>
                  <button v-else-if="r.kind === 'dept'" class="dir-d" :style="{ '--pad': r.pad + 'px' }" :title="`${r.trail ? r.trail + ' / ' : ''}${r.label} · ${r.count} 人`" @click="togDir(r.path)">
                    <i class="dir-arr" :class="{ open: r.open }">{{ r.hasKids ? '▶' : '' }}</i>
                    <b>{{ r.label }}</b>
                    <em>{{ r.count }}</em>
                  </button>
                  <button v-else class="dir-p" :style="{ '--pad': r.pad + 'px' }" :title="`${r.trail ? r.trail + ' / ' : ''}${r.label} · ${r.member!.title || r.member!.dept}`" @click="openMember(r.member!)">
                    <span class="dp-av" :style="{ background: avatarColor(r.label) }">{{ r.label[0] }}<i v-if="r.member!.online" class="dp-on" /></span>
                    <span class="dp-b"><b>{{ r.label }}</b><small v-if="r.member!.title || r.member!.dept">{{ r.member!.title || r.member!.dept }}</small></span>
                  </button>
                </template>
                <div v-if="orgLd.loading" class="dir-more"><i class="dir-spin" />加载中…</div>
              </div>

              <!-- 常用联系人 = 最近联系过的人 -->
              <div v-else-if="dirTab === 'star'" class="dir-list" @scroll="starLd.scroll">
                <button v-for="m in dirStar.slice(0, starLd.shown)" :key="m.id" class="dir-p" @click="openMember(m)">
                  <span class="dp-av" :style="{ background: avatarColor(m.name) }">{{ m.name[0] }}<i v-if="m.online" class="dp-on" /></span>
                  <span class="dp-b"><b>{{ m.name }}<i v-if="m.external" class="dp-ext">外</i></b><small v-if="m.title || m.dept">{{ m.title || m.dept }}</small></span>
                  <time>{{ fmtAgo(m.lastContactMin!) }}</time>
                </button>
                <div v-if="starLd.loading" class="dir-more"><i class="dir-spin" />加载中…</div>
              </div>

              <!-- 外部伙伴：按人平铺（与「常用」一致），职位@公司名；仅展示我负责的伙伴成员 -->
              <div v-else class="dir-list" @scroll="extLd.scroll">
                <button v-for="r in extRows.slice(0, extLd.shown)" :key="r.key" class="dir-p" :title="`${r.title}@${r.co}`" @click="openMember(r.member)">
                  <span class="dp-av" :style="{ background: avatarColor(r.name) }">{{ r.name[0] }}<i v-if="r.member.online" class="dp-on" /></span>
                  <span class="dp-b"><b>{{ r.name }}<i class="dp-ext">外</i></b><small>{{ r.title }}@{{ r.co }}</small></span>
                </button>
                <div v-if="extLd.loading" class="dir-more"><i class="dir-spin" />加载中…</div>
              </div>
            </template>
          </div>
        </section>
      </div>
    </template>

    <!-- ============ 产研页（/product：左栏直接渲染产研导航，替代原页内侧栏） ============ -->
    <template v-else-if="railMode === 'sol'">
      <div class="rail-label"><span>产研工作台</span></div>
      <!-- never-collapse：常驻左栏，禁用 UNavTree 自身的 ≤960px 窄屏折叠 -->
      <UNavTree class="sol-railnav" :groups="SOL_GROUPS" :active-id="solTab"
                fav-key="ema.sol.navfavs.v1" never-collapse @select="goSol" />
    </template>

    <!-- ============ 应用页（/apps：传统系统功能菜单） ============ -->
    <template v-else>
      <div v-if="menuError" role="alert" class="rail-empty">{{ menuError }} <button :disabled="menuLoading" @click="loadMenus">重试</button></div>
      <!-- 菜单搜索（功能名/模块名过滤） -->
      <input ref="searchEl" v-model="menuKw" class="menu-search" type="text" placeholder="搜索菜单 / 功能…" maxlength="20">

      <!-- 搜索命中：跨模块平铺功能项 -->
      <div v-if="menuHits" class="menu-tree hits">
        <div v-if="!menuHits.length" class="rail-empty">未找到匹配的功能</div>
        <button v-for="h in menuHits" :key="h.leaf.id" class="mt-row hit" :class="{ on: leafOn(h.leaf) }"
                :title="h.leaf.name" @click="goLeaf(h.leaf)">
          <span class="mt-ic" :style="{ background: h.mod.color }">{{ h.mod.ic }}</span>
          <span class="hit-b">
            <b>{{ h.leaf.name }}</b>
            <small>{{ h.mod.name }}{{ h.leaf.stub ? ' · 规划中' : '' }}</small>
          </span>
        </button>
      </div>

      <template v-else>
        <!-- 常用菜单（收藏置顶，位于搜索栏与系统菜单之间；空态给收藏引导） -->
        <div class="rail-label"><span>常用菜单</span> <span class="ct">{{ favs.length }}</span></div>
        <div v-if="!menuError" class="rail-chats favs">
          <button v-for="f in favs" :key="f.code" class="fav-item"
                  :class="{ on: leafBy[f.code] ? leafOn(leafBy[f.code].leaf) : false }"
                  :title="f.name" @click="leafBy[f.code] && goLeaf(leafBy[f.code].leaf)">
            <span class="fv-ic" :style="{ background: f.color }">{{ f.ic }}</span>
            <span class="fv-nm">{{ f.name }}</span>
            <i class="fv-x" title="取消收藏" @click.stop="favs = favs.filter(x => x.code !== f.code)">×</i>
          </button>
          <div v-if="!favs.length" class="rail-empty">点系统菜单功能行尾的 ☆ 收藏到这里</div>
        </div>

        <!-- 系统菜单（业务模块 → 功能项）；段头带一键展开/收起 -->
        <div class="rail-label">
          <span>系统菜单</span> <span class="ct">{{ menus.length }} 模块</span>
          <button v-if="menus.length" class="lab-act" :title="allModsOpen ? '全部收起' : '全部展开'"
                  @click="togAllMods">{{ allModsOpen ? '全部收起' : '全部展开' }}</button>
        </div>
        <div class="menu-tree">
          <div v-for="m in menus" :key="m.code" class="mt-dom">
            <!-- L1 业务模块：点击展开/收起，拖拽调整模块顺序 -->
            <button class="mt-row lv1"
                    :class="[isDraggingMod(m.code) ? 'dragging' : '', hintCls('m:' + m.code)]"
                    draggable="true"
                    @click="togMod(m.code)"
                    @dragstart="dragStartMod($event, m)"
                    @dragover="dragOverRow($event, 'm:' + m.code, 'mod')"
                    @drop="dropOnMod(m)"
                    @dragend="dragEnd">
              <i class="mt-arr" :class="{ open: openMods.has(m.code) }">▶</i>
              <span class="mt-ic" :style="{ background: m.color }">{{ m.ic }}</span>
              <span class="mt-nm">{{ m.name }}</span>
            </button>
            <!-- L2 功能项：同模块内拖拽排序 -->
            <template v-if="openMods.has(m.code)">
              <button v-for="l in m.items" :key="l.id"
                      class="mt-row lv2"
                      :class="[{ on: leafOn(l), dim: l.stub }, isDraggingLeaf(l.id) ? 'dragging' : '', hintCls('l:' + l.id)]"
                      draggable="true"
                      :title="l.stub ? l.name + ' · 规划中' : l.name"
                      @click="goLeaf(l)"
                      @dragstart="dragStartLeaf($event, l, m)"
                      @dragover="dragOverRow($event, 'l:' + l.id, 'leaf', m.code)"
                      @drop="dropOnLeaf(l, m)"
                      @dragend="dragEnd">
                <span class="mt-nm">{{ l.name }}<i v-if="l.tag" class="mt-tag">{{ l.tag }}</i></span>
                <i v-if="l.stub" class="mt-stub">规划中</i>
                <i class="mt-fav" :class="{ on: isFav(l.id) }"
                   :title="isFav(l.id) ? '取消收藏' : '收藏到常用菜单'"
                   @click.stop="toggleFav($event, l, m)">{{ isFav(l.id) ? '★' : '☆' }}</i>
              </button>
            </template>
          </div>
        </div>
      </template>
    </template>

    <!-- 成员详情（居中弹窗，Teleport 到 body） -->
    <Teleport to="body">
      <div v-if="dirSel" class="dir-mask" @click.self="dirSel = null">
        <div class="dir-modal">
          <button class="dm-x" title="关闭" @click="dirSel = null">×</button>
          <div class="dm-hero">
            <span class="dm-av" :style="{ background: avatarColor(dirSel.name) }">{{ dirSel.name[0] }}</span>
            <div class="dm-t">
              <p v-if="profileError" role="alert">{{profileError}}</p><h3>{{ dirSel.name }}
                <i v-if="dirSel.online" class="dm-on">在线</i>
                <i v-if="dirSel.external" class="dm-ext">外部 · {{ dirSel.co }}</i>
              </h3>
              <p>{{ dirSel.title || (dirSel.external ? dirSel.co : dirSel.dept) }}</p>
            </div>
          </div>
          <dl class="dm-kv">
            <template v-if="!dirSel.external">
              <dt>所属部门</dt><dd>{{ dirSel.deptPath.join(' / ') }}</dd>
              <dt>手机</dt><dd>{{ dirSel.phone }}</dd>
              <dt>企业工号</dt><dd>{{ dirSel.empNo }}</dd>
              <template v-if="dirSel.partTime?.length">
                <dt>兼职</dt><dd>{{ dirSel.partTime.map(x => `${x.dept} · ${x.title}`).join('；') }}</dd>
              </template>
              <dt>入职日期</dt><dd>{{ dirSel.joinDate }}</dd>
            </template>
            <template v-else>
              <dt>所属公司</dt><dd>{{ dirSel.co }}</dd>
              <dt>手机</dt><dd>{{ dirSel.phone }}</dd>
            </template>
            <dt>角色</dt><dd class="dm-roles"><span v-for="r in dirSel.roles" :key="r">{{ r }}</span></dd>
          </dl>

          <!-- 岗位职责与义务 -->
          <div class="dm-sec">
            <h4>岗位职责与义务</h4>
            <ul v-if="dirProf?.duties.length" class="dm-duties">
              <li v-for="d in dirProf.duties" :key="d">{{ d }}</li>
            </ul>
            <p v-else class="dm-hint">正在载入…</p>
          </div>

          <!-- 进行中的工作 -->
          <div class="dm-sec">
            <h4>进行中的工作</h4>
            <div v-if="dirProf?.works.length" class="dm-works">
              <div v-for="w in dirProf.works" :key="w.name" class="dm-work">
                <div class="dmw-t"><b>{{ w.name }}</b><em>{{ w.prog }}%</em></div>
                <small>{{ w.node }} · {{ w.due }}</small>
                <span class="dmw-bar"><i :style="{ width: w.prog + '%' }" /></span>
              </div>
            </div>
            <p v-else class="dm-hint">正在载入…</p>
          </div>

          <div class="dm-acts">
            <button class="ubtn primary" @click="dirMessage(dirSel)">发消息</button>
            <button class="ubtn secondary" @click="dirSel = null">关闭</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 讨论行悬停完整信息卡（Teleport 到 body，避免被左栏裁剪） -->
    <Teleport to="body">
      <div v-if="hoverCard" class="rail-hovcard" :style="hcStyle()">
        <div class="hc-t">{{ hoverCard.title }}</div>
        <div class="hc-s"><i class="hc-dot" :style="{ background: hoverCard.color }" />{{ hoverCard.sub }}</div>
        <div v-if="hoverCard.mems.length" class="hc-ms">
          <span v-for="m in hoverCard.mems" :key="m.id" class="hc-m">
            <i :style="{ background: avatarColor(m.name) }">{{ m.name[0] }}</i>{{ m.name }}
          </span>
        </div>
      </div>
    </Teleport>

    <!-- 收窄态悬停浮出下级菜单（Teleport 到 body，贴窄轨右缘、跟随图标纵向位置） -->
    <Teleport to="body">
      <div v-if="miniFly" class="mini-fly" :style="flyStyle(miniFly.y)"
           @mouseenter="flyKeep" @mouseleave="flyCloseSoon">
        <!-- 模块 → 功能叶子直达（保持收窄态不展开） -->
        <template v-if="miniFly.kind === 'mod'">
          <div class="mf-head"><span class="rm-ic" :style="{ background: miniFly.mod.color }">{{ miniFly.mod.ic }}</span>{{ miniFly.mod.name }}</div>
          <button v-for="l in miniFly.mod.items" :key="l.id" class="mf-row"
                  :class="{ on: leafOn(l), dim: l.stub }"
                  :title="l.stub ? l.name + ' · 规划中' : l.name"
                  @click="goLeaf(l); miniFly = null">
            <span class="mf-nm">{{ l.name }}</span>
            <i v-if="l.tag" class="mt-tag">{{ l.tag }}</i>
            <i v-if="l.stub" class="mt-stub">规划中</i>
          </button>
          <div v-if="!miniFly.mod.items.length" class="mf-empty">暂无功能项</div>
        </template>
        <!-- 会话 → 最近工作会话直达 -->
        <template v-else-if="miniFly.kind === 'acc' && miniFly.key === 'chat'">
          <div class="mf-head">工作会话</div>
          <button v-for="s in recent.slice(0, 10)" :key="s.id" class="mf-row"
                  @click="openHitSession(s.id); miniFly = null">
            <span class="rh-dot" :data-health="s.health || 'ok'" />
            <span class="mf-nm">{{ s.title }}</span>
          </button>
          <div v-if="!recent.length" class="mf-empty">暂无会话</div>
        </template>
        <!-- 群组 → 临时讨论 + 固定群聊直达 -->
        <template v-else-if="miniFly.kind === 'acc' && miniFly.key === 'groups'">
          <div class="mf-head">讨论群组</div>
          <button v-for="g in [...tempGroups, ...fixGroups].slice(0, 10)" :key="g.id" class="mf-row"
                  :title="`${g.title} · ${g.member_count}人`"
                  @click="openDiscussion(g.id); miniFly = null">
            <span class="tp-dot" :style="{ background: g.kind === 'fixed' ? '#3b82f6' : (g.status === 'active' ? 'var(--accent)' : 'var(--ink-4)') }" />
            <span class="mf-nm">{{ g.title }}</span>
            <span v-if="g.unread" class="tp-unread">{{ g.unread > 99 ? '99+' : g.unread }}</span>
          </button>
          <div v-if="!tempGroups.length && !fixGroups.length" class="mf-empty">暂无讨论</div>
        </template>
        <!-- 产研分组 → 功能直达（保持收窄态不展开） -->
        <template v-else-if="miniFly.kind === 'solg'">
          <div class="mf-head"><span class="rm-ic" :style="{ background: miniFly.g.color }">{{ miniFly.g.ic }}</span>{{ miniFly.g.name }}</div>
          <button v-for="i in miniFly.g.items" :key="i.id" class="mf-row col"
                  :class="{ on: i.id === solTab }" :title="`${i.name} · ${i.sub}`"
                  @click="goSol(i.id); miniFly = null">
            <span class="mf-nm">{{ i.name }}</span>
            <span class="mf-sub">{{ i.sub }}</span>
          </button>
        </template>
        <!-- 通讯录 → 常用联系人直达 -->
        <template v-else>
          <div class="mf-head">通讯录 · 常用</div>
          <button v-for="m in dirStar.slice(0, 8)" :key="m.id" class="mf-row"
                  :title="`${m.name} · ${m.title || m.dept || ''}`"
                  @click="openMember(m); miniFly = null">
            <span class="rh-av" :style="{ background: avatarColor(m.name) }">{{ m.name[0] }}</span>
            <span class="mf-nm">{{ m.name }}</span>
          </button>
          <div v-if="!dirStar.length" class="mf-empty">暂无常用联系人</div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.railnav { display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }

/* === 收窄态（mini）：64px 图标窄轨，复刻需求目录收窄效果（48px 项 / 10px 小标签） ===
   完整内容 display:none 保留组件状态（展开/搜索词/滚动位置不丢），仅留图标轨；
   48px 项宽让 4 字菜单名（约占 40px）完整显示，更长名称仍省略号兜底 */
.railnav.mini { gap: 4px; align-items: center; overflow-y: auto; padding: 2px 0; }
.railnav.mini > :not(.rail-mini) { display: none; }
.rail-mini { display: flex; flex-direction: column; align-items: center; gap: 4px; width: 100%; }
.rmi {
  width: 48px; padding: 7px 0 5px; border-radius: 8px;
  display: grid; place-items: center; gap: 3px;
  font-size: 10px; color: var(--ink-3); cursor: pointer; text-align: center;
}
.rmi:hover { background: var(--hover); color: var(--ink-2); }
.rmi.on { background: var(--accent-soft); color: var(--accent); }
.rmi.accent { color: var(--accent); }
.rmi svg { display: block; }
.rm-ic {
  width: 21px; height: 21px; border-radius: 7px;
  color: #fff; font-size: 11px; font-weight: 700; display: grid; place-items: center;
}
.rm-nm { max-width: 100%; padding: 0 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.2; }
.rm-sep { width: 32px; height: 1px; background: var(--line); margin: 3px 0; flex: none; }
/* 收藏磁贴与模块磁贴同构，仅以分隔线区分分组；高亮规则沿用 .rmi.on */

/* === 收窄态悬停浮出下级菜单（Teleport body；图标与浮层间 140ms 宽限防断开） === */
.mini-fly {
  position: fixed; z-index: 900; width: 208px; max-height: 70vh; overflow-y: auto;
  padding: 6px; background: var(--surface); border: 1px solid var(--line);
  border-radius: 10px; box-shadow: 0 10px 30px -10px rgba(20, 22, 26, .3);
}
.mf-head {
  display: flex; align-items: center; gap: 8px; padding: 6px 8px 8px; margin-bottom: 4px;
  font-size: 12px; font-weight: 600; color: var(--ink); border-bottom: 1px solid var(--line-soft);
}
.mf-row {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 7px 8px; border-radius: 7px; font-size: 12.3px; color: var(--ink-2); text-align: left;
}
.mf-row.col { flex-direction: column; align-items: stretch; gap: 2px; }
.mf-row:hover { background: var(--hover); }
.mf-row.on { background: var(--accent-soft); color: var(--accent); font-weight: 550; }
.mf-row.dim .mf-nm { color: var(--ink-4); }
.mf-nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mf-sub { font-size: 10.5px; color: var(--ink-4); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mf-empty { padding: 10px; font-size: 11px; color: var(--ink-4); text-align: center; }

/* === 产研模式（/product）：UNavTree 占满左栏高度自滚 === */
.sol-railnav { flex: 1; min-height: 0; width: 100%; }

/* === 新建会话 === */
.new-chat {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 11px 12px; border-radius: var(--r-m);
  background: var(--accent-solid); color: #fff;
  font-size: var(--fs-base); font-weight: var(--fw-semibold); letter-spacing: .02em;
  box-shadow: 0 6px 16px -8px color-mix(in srgb, var(--accent) 55%, transparent);
  transition: filter .15s ease, transform .15s ease, box-shadow .15s ease;
}
.new-chat:hover { filter: brightness(1.08); transform: translateY(-1px); box-shadow: 0 10px 22px -8px color-mix(in srgb, var(--accent) 65%, transparent); }
.new-chat .k {
  font-size: 10px; padding: 2px 6px; border-radius: 5px;
  background: #ffffff2b; border: 1px solid #ffffff38;
}

/* === 左栏全局搜索结果 === */
.rail-hits { display: flex; flex-direction: column; gap: 1px; max-height: 46vh; overflow-y: auto; padding: 0 0 4px; }
.rh-label { padding: 7px 10px 2px; font-size: var(--fs-2xs); color: var(--ink-4); }
.rh-row {
  display: flex; align-items: center; gap: 8px;
  width: calc(100% - 12px); margin: 0 auto; padding: 7px 10px;
  border: 0; border-radius: 9px; background: transparent; color: var(--ink-2);
  font: inherit; font-size: var(--fs-sm); text-align: left; cursor: pointer;
  transition: background .14s ease, box-shadow .14s ease;
}
.rh-row:hover { background: var(--surface); box-shadow: 0 2px 8px -2px #00000014; }
.rh-nm { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: var(--fw-medium); }
.rh-note { flex: none; margin-left: auto; font-size: var(--fs-2xs); color: var(--ink-4); }
.rh-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); flex: none; }
.rh-dot[data-health=blocked] { background: var(--danger, #b42318); }
.rh-dot[data-health=attention] { background: var(--warn); }
.rh-av { width: 20px; height: 20px; border-radius: 50%; color: #fff; font-size: 10px; display: grid; place-items: center; flex: none; }

/* === 分组标签 === */
.rail-label {
  display: flex; align-items: baseline; gap: 6px;
  padding: 0 2px; font-size: 10.5px; font-weight: 600;
  color: var(--ink-4); letter-spacing: .7px;
}
.rail-label .ct { color: var(--ink-4); font-weight: 400; }
/* 段头动作按钮（一键展开/收起全部模块） */
.lab-act {
  margin-left: auto; align-self: center;
  font-size: 10px; font-weight: 550; letter-spacing: 0;
  color: var(--ink-4); padding: 2px 8px; border-radius: 999px; cursor: pointer;
}
.lab-act:hover { color: var(--accent); background: var(--accent-soft); }

/* === 近期会话 / 常用菜单列表 === */
.rail-chats { flex: 1; overflow: auto; min-height: 0; display: flex; flex-direction: column; gap: 3px; }
/* 应用页常用菜单：按内容自适应（不超过约 1/3 高），剩余空间让给系统菜单树 */
.rail-chats.favs { flex: none; max-height: 30vh; }
/* 聊天分组内的近期会话列表：直接挂在段体上，减少一层嵌套并前移缩进；右侧留边并禁止横向溢出 */
.acc-body.chats { padding: 2px 12px 8px 12px; max-height: 46vh; overflow-y: auto; overflow-x: hidden; }
.rail-chat {
  display: flex; align-items: center; gap: 9px; width: 100%;
  padding: 9px 12px; border-radius: var(--r-s);
  font-size: var(--fs-base); color: var(--ink-2); text-align: left;
  transition: background .14s ease, color .14s ease;
}
.rail-chat:hover { background: var(--hover); }
.rail-chat.on { background: var(--accent-soft); color: var(--accent); font-weight: var(--fw-semibold); }
.rc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); flex: none; }
.rc-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rail-empty { padding: var(--sp-3); font-size: var(--fs-sm); color: var(--ink-4); text-align: center; line-height: 1.7; }

/* 常用菜单项 */
.fav-item {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 6px 8px; border-radius: var(--r-s);
  font-size: var(--fs-xs); color: var(--ink-2); text-align: left;
}
.fav-item:hover { background: var(--hover); }
.fav-item.on { background: var(--accent-soft); color: var(--accent); }
.fv-ic {
  width: 18px; height: 18px; flex: none; border-radius: 5px;
  color: #fff; font-size: 10px; font-weight: 650; display: grid; place-items: center;
}
.fv-nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fv-x { flex: none; font-style: normal; font-size: 13px; color: var(--ink-4); width: 16px; text-align: center; opacity: 0; }
.fav-item:hover .fv-x { opacity: 1; }
.fv-x:hover { color: #de4343; }

/* === 系统菜单树（行规格与 /arch 应用架构目录树 .abtr 对齐：7px 12px / 12.8px / 图标 24px） ===
   层级引导线：在每个祖先层级缩进档位中点画 1px 竖线（缩进步长 24px，线位 24/48/72）；
   引导线用 background-image 实现，故 hover/选中态必须用 background-color 以免简写清掉竖线 */
.menu-tree { flex: 1; overflow: auto; min-height: 0; display: flex; flex-direction: column; gap: 1px; }
.mt-row {
  display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
  padding: 7px 12px; border-radius: var(--r-s);
  font-size: 12.8px; color: var(--ink-2); min-width: 0;
}
.mt-row:hover { background-color: var(--hover); }
.mt-row.on { background-color: var(--accent-soft); color: var(--accent); font-weight: 550; }
.mt-row.on .mt-nm { color: var(--accent); }
.mt-row.lv1 { font-weight: 550; color: var(--ink); }
.mt-row.lv2 {
  padding-left: 36px; color: var(--ink-3);
  background: linear-gradient(var(--line-soft), var(--line-soft)) no-repeat 24px 0 / 1px 100%;
}
.mt-row.lv3 {
  padding-left: 60px;
  background:
    linear-gradient(var(--line-soft), var(--line-soft)) no-repeat 24px 0 / 1px 100%,
    linear-gradient(var(--line-soft), var(--line-soft)) no-repeat 48px 0 / 1px 100%;
}
.mt-arr { font-style: normal; font-size: 8.5px; width: 11px; height: 11px; flex: none; color: var(--ink-4); display: grid; place-items: center; transition: transform .15s; line-height: 1; }
.mt-arr.open { transform: rotate(90deg); color: var(--accent); }
/* === 应用菜单搜索 + 命中 === */
.menu-search {
  height: 30px; padding: 0 10px; font-size: var(--fs-xs);
  width: calc(100% - 20px); margin: 0 auto; box-sizing: border-box; flex: none;
  border: 1px solid var(--line); border-radius: 8px;
  background: var(--surface); color: var(--ink);
}
.menu-search:focus { border-color: var(--accent-line); outline: none; }
.mt-row.hit { padding-left: 12px; }
.hit-b { flex: 1; min-width: 0; }
.hit-b b { display: block; font-size: 12.8px; font-weight: 550; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hit-b small { display: block; font-size: 10.3px; color: var(--ink-4); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mt-tag { font-style: normal; margin-left: 5px; padding: 0 4px; border-radius: 4px; font-size: 8.5px; font-weight: 700; color: var(--accent); background: var(--accent-soft); vertical-align: 1px; }
.mt-stub { flex: none; font-style: normal; font-size: 8.5px; color: var(--ink-4); border: 1px solid var(--line-soft); border-radius: 4px; padding: 0 4px; }
.mt-row.dim .mt-nm { color: var(--ink-4); }
.mt-ic {
  width: 21px; height: 21px; flex: none; border-radius: 7px;
  color: #fff; font-size: 11px; font-weight: 700; display: grid; place-items: center;
}
.mt-nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.mt-fav { flex: none; font-style: normal; font-size: 14.5px; color: var(--ink-4); width: 20px; text-align: center; transition: color .12s ease; cursor: pointer; }
.mt-row:hover .mt-fav { color: var(--ink-3); }
.mt-fav.on { color: #f5a623; }
.mt-fav:hover { color: #f5a623; }

/* === 菜单拖拽排序：抓取光标 + 插入位置指示线（上半区=插到前，下半区=插到后） === */
.mt-row[draggable="true"] { cursor: grab; }
.mt-row[draggable="true"]:active { cursor: grabbing; }
.mt-row.dragging { opacity: .45; }
.mt-row.drop-before { box-shadow: inset 0 2px 0 var(--accent); }
.mt-row.drop-after { box-shadow: inset 0 -2px 0 var(--accent); }

/* === 单开分组（acc = 分组段） === */
.acc-list { display: flex; flex-direction: column; gap: 3px; min-height: 0; }
.acc { border-radius: 10px; }
.acc-head {
  width: 100%; display: flex; align-items: center; gap: 9px;
  padding: 9px 12px; border-radius: 10px; text-align: left;
  transition: background .14s ease;
}
.acc-head:hover { background: var(--hover); }
.acc-ic { color: var(--ink-3); flex: none; width: 17px; height: 17px; }
.acc-nm { font-size: var(--fs-base); font-weight: var(--fw-semibold); color: var(--ink-2); }
.acc.on .acc-head { background: var(--accent-soft); }
.acc.on .acc-nm, .acc.on .acc-ic { color: var(--accent); }
.acc-ct {
  margin-left: auto; font-style: normal; font-size: var(--fs-xs);
  color: var(--ink-4); font-variant-numeric: tabular-nums; flex: none;
}
/* 计数非空时呈现为小徽章，空态不占位 */
.acc-ct:not(:empty) {
  min-width: 20px; text-align: center; padding: 1px 7px; border-radius: 999px;
  background: var(--surface-3); border: 1px solid var(--line-soft);
  font-size: var(--fs-2xs); font-weight: var(--fw-semibold); color: var(--ink-3); line-height: 16px;
}
.acc.on .acc-ct:not(:empty) { background: var(--surface); color: var(--accent); border-color: var(--accent-line); }
.acc-ct.warn:not(:empty) { background: var(--amber-soft); color: var(--warn); border-color: transparent; }
.acc-arr {
  font-style: normal; font-size: 16px; color: var(--ink-4);
  width: 16px; text-align: center; flex: none; line-height: 1;
  transition: transform .16s ease;
}
.acc.open .acc-arr { transform: rotate(90deg); }
/* 分组段体：左右留白对称（内容随段体居中），不再缩进、不再画左缘引导线 */
.acc-body {
  padding: 2px 6px 8px; display: flex; flex-direction: column; gap: 5px; min-height: 0;
}
.topic { display: flex; align-items: flex-start; gap: 8px; text-align: left; padding: 7px 8px; border-radius: 8px; transition: background .14s ease; }
.topic:hover { background: var(--hover); }
/* 讨论群组双页签：临时讨论 / 固定群组 */
.tp-tabs { display: flex; gap: 4px; margin: 2px 0 6px; padding: 3px; background: var(--surface-2); border-radius: 8px; }
.tp-tabs button { flex: 1; border: none; background: none; cursor: pointer; padding: 6px 0; font-size: var(--fs-sm); color: var(--ink-3); border-radius: 6px; }
.tp-tabs button.on { background: var(--surface); color: var(--ink); font-weight: 600; box-shadow: 0 1px 2px rgba(0, 0, 0, .07); }
/* 话题行右侧：参与人头像堆叠（前 5 + N），悬停 title 列全部姓名 */
.tp-avs { display: flex; align-items: center; flex: none; margin-left: auto; }
.tp-avs i, .tp-avs em { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid var(--surface); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 600; color: #fff; font-style: normal; }
.tp-avs i + i, .tp-avs i + em { margin-left: -6px; }
.tp-avs em { background: var(--surface-3); color: var(--ink-3); font-size: 9.5px; }

/* 讨论行悬停完整信息卡（Teleport 到 body）：完整标题 + 状态元信息 + 全部成员 */
.rail-hovcard {
  position: fixed; z-index: 999; pointer-events: none;
  max-width: 264px; padding: 10px 12px;
  background: var(--surface); border: 1px solid var(--line); border-radius: 10px;
  box-shadow: 0 10px 30px -10px rgba(20, 22, 26, .3);
}
.rail-hovcard .hc-t { font-size: var(--fs-sm); font-weight: 600; color: var(--ink); line-height: 1.45; margin-bottom: 5px; }
.rail-hovcard .hc-s { display: flex; align-items: center; gap: 6px; font-size: var(--fs-xs); color: var(--ink-3); }
.rail-hovcard .hc-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.rail-hovcard .hc-ms { display: flex; flex-wrap: wrap; gap: 5px 12px; margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--line-soft); }
.rail-hovcard .hc-m { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-xs); color: var(--ink-2); }
.rail-hovcard .hc-m i { width: 17px; height: 17px; border-radius: 50%; color: #fff; font-size: 9.5px; font-weight: 600; display: flex; align-items: center; justify-content: center; font-style: normal; flex: none; }
.tp-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; margin-top: 6px; }
/* 讨论未读徽标 */
.tp-unread { flex: none; align-self: center; min-width: 18px; text-align: center; padding: 1px 6px; border-radius: 999px; background: var(--accent); color: #fff; font-size: 10px; font-weight: 600; line-height: 15px; }
.topic:has(.tp-avs) { align-items: center; }
.topic:has(.tp-avs) .tp-dot { margin-top: 0; }
.tp-b { min-width: 0; flex: 1; }
.tp-t { display: block; font-size: var(--fs-sm); color: var(--ink-2); font-weight: var(--fw-semibold); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tp-b small { display: block; margin-top: 1px; font-size: var(--fs-xs); color: var(--ink-4); }
.wv-row {
  display: flex; align-items: center; gap: 7px; text-align: left;
  padding: 4px 6px; border-radius: 6px;
}
.wv-row:hover { background: var(--hover); }
.wv-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent-solid); flex: none; }
.wv-row-b b { display: block; font-size: var(--fs-2xs); color: var(--ink); font-weight: 550; }
.wv-row-b small { display: block; font-size: 9.5px; color: var(--ink-4); }
.wv-open {
  align-self: flex-start; padding: 3px 2px; font-size: var(--fs-xs);
  color: var(--accent); font-weight: var(--fw-semibold);
}
.wv-open:hover { text-decoration: underline; }

/* === 企业通讯录（聊天左栏内嵌全功能） ===
   窄栏下的层级表达：只用缩进 + 字重，不再画层级引导线或彩色色块；
   缩进步长 12px、封顶 3 级（更深的层级保持同一缩进），文字一律单行省略，
   因此层数再多也不会把右侧文字挤到看不见。 */
.dir-body { padding: 4px 8px 10px; }
.dir-search { margin: 1px 0 8px; display: flex; align-items: center; gap: 6px; }
.dir-search .menu-search { flex: 1; min-width: 0; box-sizing: border-box; font-size: var(--fs-sm); padding: 6px 10px; }
.dir-tabs { display: flex; gap: 3px; background: var(--surface-2); border-radius: 8px; padding: 3px; flex: none; }
.dir-tabs button { flex: 1; padding: 6px 0; font-size: var(--fs-sm); color: var(--ink-2); border-radius: 6px; }
.dir-tabs button.on { background: var(--surface); color: var(--accent); font-weight: 600; box-shadow: 0 1px 3px #0002; }
.dir-list { display: flex; flex-direction: column; gap: 1px; padding: 3px 0 2px; max-height: 46vh; overflow-y: auto; min-height: 0; overscroll-behavior: contain; }
.dir-arr { font-style: normal; font-size: 7.5px; width: 10px; height: 10px; flex: none; color: var(--ink-4); display: grid; place-items: center; transition: transform .15s; line-height: 1; }
.dir-arr.open { transform: rotate(90deg); color: var(--accent); }
/* 树行：--pad 由模板按层级注入（根 8 / 一至七级 16·28·38·46·52·57·61 / 人员 65；更深不再缩进） */
.dir-c, .dir-d, .dir-p {
  display: flex; align-items: center; gap: 7px; width: 100%; min-width: 0; flex: none;
  padding: 4px 8px 4px var(--pad, 8px); border-radius: 7px; text-align: left;
  font-size: 12.3px; line-height: 1.3; color: var(--ink-2);
}
.dir-c.root { font-size: 12.6px; font-weight: 600; color: var(--ink); }
.dir-c.lv2, .dir-d { font-weight: 550; }
.dir-c b, .dir-d b { flex: 1; min-width: 0; font-weight: inherit; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dir-c em, .dir-d em { flex: none; font-style: normal; font-size: 10.5px; color: var(--ink-4); font-variant-numeric: tabular-nums; }
.dir-c:hover, .dir-d:hover, .dir-p:hover { background-color: var(--hover); }
.dp-av {
  width: 20px; height: 20px; border-radius: 6px; flex: none; position: relative;
  display: grid; place-items: center; color: #fff; font-size: 10.5px; font-weight: 700;
}
.dp-on { position: absolute; right: -2px; bottom: -2px; width: 6px; height: 6px; border-radius: 50%; background: #22b573; border: 1.5px solid var(--surface); }
.dp-b { flex: 1; min-width: 0; }
.dp-b b { display: flex; align-items: center; gap: 5px; font-size: 12.3px; font-weight: 550; line-height: 1.25; color: var(--ink-2); white-space: nowrap; overflow: hidden; }
.dp-b small { display: block; font-size: 10px; line-height: 1.3; margin-top: 1px; color: var(--ink-4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp-ext { flex: none; font-style: normal; font-size: 9px; color: var(--violet); background: color-mix(in srgb, var(--violet) 10%, transparent); border-radius: 999px; padding: 0 4px; line-height: 13px; }
.dir-p time { flex: none; font-size: 10px; color: var(--ink-4); }
.dir-more { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 0 6px; font-size: 10px; color: var(--ink-4); }
.dir-spin { width: 10px; height: 10px; border: 1.5px solid var(--line); border-top-color: var(--accent); border-radius: 50%; animation: dir-spin .7s linear infinite; }
@keyframes dir-spin { to { transform: rotate(360deg) } }
.dir-empty { padding: 14px 0; text-align: center; font-size: 10.5px; color: var(--ink-4); }

/* 成员详情弹窗（Teleport body，scoped 属性随元素一起传送） */
.dir-mask { position: fixed; inset: 0; z-index: 800; background: rgba(15, 21, 36, .5); backdrop-filter: blur(2px); display: grid; place-items: center; }
.dir-modal { width: 380px; max-width: calc(100vw - 32px); max-height: 76vh; overflow-y: auto; background: var(--surface); border-radius: 12px; padding: 18px; box-shadow: var(--sh-3); position: relative; }
.dm-x { position: absolute; top: 10px; right: 12px; font-size: 16px; color: var(--ink-4); }
.dm-x:hover { color: var(--ink); }
.dm-hero { display: flex; align-items: center; gap: 12px; padding-bottom: 12px; border-bottom: 1px solid var(--line-soft); }
.dm-av { width: 46px; height: 46px; border-radius: 10px; flex: none; display: grid; place-items: center; color: #fff; font-size: 18px; font-weight: 600; }
.dm-t h3 { font-size: 14.5px; color: var(--ink); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.dm-t p { font-size: 11.5px; color: var(--ink-3); margin-top: 2px; }
.dm-on { font-style: normal; font-size: 9.5px; color: #16a34a; background: color-mix(in srgb, #16a34a 10%, transparent); border-radius: 999px; padding: 1px 7px; }
.dm-ext { font-style: normal; font-size: 9.5px; color: var(--violet); background: color-mix(in srgb, var(--violet) 10%, transparent); border-radius: 999px; padding: 1px 7px; }
.dm-kv { margin: 12px 0; display: grid; grid-template-columns: 64px 1fr; row-gap: 7px; font-size: 11.5px; }
.dm-kv dt { color: var(--ink-4); }
.dm-kv dd { color: var(--ink-2); line-height: 1.55; }
.dm-roles { display: flex; gap: 4px; flex-wrap: wrap; }
.dm-roles span { font-size: 9.5px; color: var(--ink-3); background: var(--surface-2); border: 1px solid var(--line-soft); border-radius: 999px; padding: 1px 8px; }
/* 档案细化段：岗位职责 / 进行中的工作 */
.dm-sec { margin-top: 13px; padding-top: 11px; border-top: 1px solid var(--line-soft); }
.dm-sec h4 { margin: 0 0 8px; font-size: 10.5px; font-weight: 600; color: var(--ink-4); letter-spacing: .04em; }
.dm-duties { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 6px; }
.dm-duties li { position: relative; padding-left: 13px; font-size: 11.5px; line-height: 1.55; color: var(--ink-2); }
.dm-duties li::before { content: ''; position: absolute; left: 1px; top: 7px; width: 5px; height: 5px; border-radius: 50%; background: var(--accent-line); }
.dm-works { display: flex; flex-direction: column; gap: 10px; }
.dmw-t { display: flex; align-items: baseline; gap: 8px; }
.dmw-t b { flex: 1; min-width: 0; font-size: 12px; font-weight: 550; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dmw-t em { flex: none; font-style: normal; font-size: 10.5px; font-weight: 600; color: var(--accent); }
.dm-work small { display: block; margin: 2px 0 5px; font-size: 10px; color: var(--ink-4); }
.dmw-bar { display: block; height: 4px; border-radius: 999px; background: var(--surface-2); overflow: hidden; }
.dmw-bar i { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--accent-line), var(--accent)); }
.dm-hint { margin: 0; font-size: 10.5px; color: var(--ink-4); }
.dm-acts { display: flex; gap: 8px; margin-top: 14px; }
</style>
